import React, { useEffect, useState } from 'react';
export function SystemQuote({
  priceType,
  onAddToCart,
  pricingData,
  onPendingItemsChange,
  showPricing
}) {
  const [selectedSystems, setSelectedSystems] = useState([]);
  // Notify parent component when pending items status changes
  useEffect(() => {
    onPendingItemsChange(selectedSystems.length > 0);
  }, [selectedSystems, onPendingItemsChange]);
  const addSystem = type => {
    const newSystem = {
      type,
      id: Date.now(),
      quantity: 1,
      chestGuardSizes: [],
      headGearSizes: [],
      chargers: 1,
      judgeBoxes: 0
    };
    // Judge boxes default to 0 — user can add as needed
    // Add default chest guard sizes for systems that include body
    if (type === 'full' || type === 'judgeAndBody' || type === 'bodyOnly') {
      newSystem.chestGuardSizes = [{
        size: 'size2',
        color: 'red',
        quantity: 1
      }, {
        size: 'size2',
        color: 'blue',
        quantity: 1
      }];
    }
    // Add default head gear sizes for full systems
    if (type === 'full') {
      newSystem.headGearSizes = [{
        size: 'M',
        color: 'red',
        quantity: 1
      }, {
        size: 'M',
        color: 'blue',
        quantity: 1
      }];
    }
    // Add new system at the beginning of the array so it appears at the top
    setSelectedSystems([newSystem, ...selectedSystems]);
  };
  const removeSystem = id => {
    setSelectedSystems(selectedSystems.filter(system => system.id !== id));
  };
  const updateSystemQuantity = (id, quantity) => {
    setSelectedSystems(selectedSystems.map(system => {
      if (system.id === id) {
        return {
          ...system,
          quantity: parseInt(quantity) || 1
        };
      }
      return system;
    }));
  };
  const updateSystemChargers = (id, chargers) => {
    setSelectedSystems(selectedSystems.map(system => {
      if (system.id === id) {
        return {
          ...system,
          chargers: parseInt(chargers) || 1
        };
      }
      return system;
    }));
  };
  const updateSystemJudgeBoxes = (id, judgeBoxes) => {
    setSelectedSystems(selectedSystems.map(system => {
      if (system.id === id) {
        return {
          ...system,
          judgeBoxes: parseInt(judgeBoxes) || 0
        };
      }
      return system;
    }));
  };
  const addChestGuardSize = (systemId, size, color, quantity) => {
    setSelectedSystems(selectedSystems.map(system => {
      if (system.id === systemId) {
        const existingSizeIndex = system.chestGuardSizes.findIndex(item => item.size === size && item.color === color);
        if (existingSizeIndex >= 0) {
          const updatedSizes = [...system.chestGuardSizes];
          updatedSizes[existingSizeIndex] = {
            ...updatedSizes[existingSizeIndex],
            quantity: parseInt(quantity) || 1
          };
          return {
            ...system,
            chestGuardSizes: updatedSizes
          };
        } else {
          return {
            ...system,
            chestGuardSizes: [...system.chestGuardSizes, {
              size,
              color,
              quantity: parseInt(quantity) || 1
            }]
          };
        }
      }
      return system;
    }));
  };
  const removeChestGuardSize = (systemId, size, color) => {
    setSelectedSystems(selectedSystems.map(system => {
      if (system.id === systemId) {
        return {
          ...system,
          chestGuardSizes: system.chestGuardSizes.filter(item => !(item.size === size && item.color === color))
        };
      }
      return system;
    }));
  };
  const addHeadGearSize = (systemId, size, color, quantity) => {
    setSelectedSystems(selectedSystems.map(system => {
      if (system.id === systemId) {
        const existingSizeIndex = system.headGearSizes.findIndex(item => item.size === size && item.color === color);
        if (existingSizeIndex >= 0) {
          const updatedSizes = [...system.headGearSizes];
          updatedSizes[existingSizeIndex] = {
            ...updatedSizes[existingSizeIndex],
            quantity: parseInt(quantity) || 1
          };
          return {
            ...system,
            headGearSizes: updatedSizes
          };
        } else {
          return {
            ...system,
            headGearSizes: [...system.headGearSizes, {
              size,
              color,
              quantity: parseInt(quantity) || 1
            }]
          };
        }
      }
      return system;
    }));
  };
  const removeHeadGearSize = (systemId, size, color) => {
    setSelectedSystems(selectedSystems.map(system => {
      if (system.id === systemId) {
        return {
          ...system,
          headGearSizes: system.headGearSizes.filter(item => !(item.size === size && item.color === color))
        };
      }
      return system;
    }));
  };
  // Helper function to get head guard price by size and color
  const getHeadGuardPrice = (size, color) => {
    const headGuardId = `headGuard${size}${color.charAt(0).toUpperCase() + color.slice(1)}`;
    const headGuard = pricingData.find(item => item.id === headGuardId);
    return headGuard ? headGuard[priceType] : 399.99;
  };
  const calculateSystemTotal = system => {
    let total = 0;
    const {
      type,
      quantity,
      chestGuardSizes,
      headGearSizes
    } = system;
    // Common items for all systems
    const software = pricingData.find(item => item.id === 'software');
    const receiver = pricingData.find(item => item.id === 'receiver');
    total += software[priceType] * quantity;
    total += receiver[priceType] * quantity;
    if (type === 'full' || type === 'judgeAndBody' || type === 'bodyOnly') {
      const chestTransmitter = pricingData.find(item => item.id === 'chestTransmitter');
      total += chestTransmitter[priceType] * quantity * 2; // 2 transmitters per system
      // Add chest guards based on selected sizes
      chestGuardSizes.forEach(({
        size,
        quantity: sizeQuantity
      }) => {
        // Map individual sizes to the corresponding chest guard product
        const sizeGroup = getSizeGroup(size);
        const sizeKey = `chestGuard${sizeGroup}`;
        const chestGuard = pricingData.find(item => item.id === sizeKey);
        if (chestGuard) {
          total += chestGuard[priceType] * quantity * sizeQuantity;
        }
      });
    }
    if (type === 'full') {
      const headTransmitter = pricingData.find(item => item.id === 'headTransmitter');
      total += headTransmitter[priceType] * quantity * 2; // 2 transmitters per system
      // Add head gear based on selected sizes
      headGearSizes.forEach(({
        size,
        color,
        quantity: sizeQuantity
      }) => {
        const headGuardPrice = getHeadGuardPrice(size, color);
        total += headGuardPrice * quantity * sizeQuantity;
      });
    }
    if (type === 'full' || type === 'judgeAndBody' || type === 'judgeOnly') {
      const judgeBox = pricingData.find(item => item.id === 'judgeBox');
      total += judgeBox[priceType] * quantity * (system.judgeBoxes || 0);
    }
    return total.toFixed(2);
  };
  const handleAddToQuote = () => {
    let cartItems = {};
    selectedSystems.forEach(system => {
      const {
        type,
        quantity,
        headGearSizes,
        chestGuardSizes,
        chargers
      } = system;
      // Common items for all systems
      if (!cartItems['software']) cartItems['software'] = 0;
      cartItems['software'] += quantity;
      if (!cartItems['receiver']) cartItems['receiver'] = 0;
      cartItems['receiver'] += quantity;
      // Add chargers
      if (!cartItems['charger']) cartItems['charger'] = 0;
      cartItems['charger'] += (chargers || 1) * quantity;
      if (type === 'full') {
        // Full Ring
        if (!cartItems['chestTransmitter']) cartItems['chestTransmitter'] = 0;
        cartItems['chestTransmitter'] += quantity * 2;
        if (!cartItems['headTransmitter']) cartItems['headTransmitter'] = 0;
        cartItems['headTransmitter'] += quantity * 2;
        if (system.judgeBoxes > 0) {
          if (!cartItems['judgeBox']) cartItems['judgeBox'] = 0;
          cartItems['judgeBox'] += quantity * system.judgeBoxes;
        }
        // Add head gears based on selected sizes
        headGearSizes.forEach(({
          size,
          color,
          quantity: sizeQuantity
        }) => {
          // Map to individual head guard items
          const headGearId = `headGuard${size}${color.charAt(0).toUpperCase() + color.slice(1)}`;
          if (!cartItems[headGearId]) cartItems[headGearId] = 0;
          cartItems[headGearId] += quantity * sizeQuantity;
        });
        // Add chest guards based on selected sizes
        chestGuardSizes.forEach(({
          size,
          color,
          quantity: sizeQuantity
        }) => {
          // Map to individual chest guard items
          const sizeNumber = size.replace('size', '');
          const chestGuardId = `chestGuardSize${sizeNumber}${color.charAt(0).toUpperCase() + color.slice(1)}`;
          if (!cartItems[chestGuardId]) cartItems[chestGuardId] = 0;
          cartItems[chestGuardId] += quantity * sizeQuantity;
        });
      } else if (type === 'judgeAndBody') {
        // Judge and Body
        if (!cartItems['chestTransmitter']) cartItems['chestTransmitter'] = 0;
        cartItems['chestTransmitter'] += quantity * 2;
        if (system.judgeBoxes > 0) {
          if (!cartItems['judgeBox']) cartItems['judgeBox'] = 0;
          cartItems['judgeBox'] += quantity * system.judgeBoxes;
        }
        // Add chest guards based on selected sizes
        chestGuardSizes.forEach(({
          size,
          color,
          quantity: sizeQuantity
        }) => {
          // Map to individual chest guard items
          const sizeNumber = size.replace('size', '');
          const chestGuardId = `chestGuardSize${sizeNumber}${color.charAt(0).toUpperCase() + color.slice(1)}`;
          if (!cartItems[chestGuardId]) cartItems[chestGuardId] = 0;
          cartItems[chestGuardId] += quantity * sizeQuantity;
        });
      } else if (type === 'bodyOnly') {
        // Body Only
        if (!cartItems['chestTransmitter']) cartItems['chestTransmitter'] = 0;
        cartItems['chestTransmitter'] += quantity * 2;
        // Add chest guards based on selected sizes
        chestGuardSizes.forEach(({
          size,
          color,
          quantity: sizeQuantity
        }) => {
          // Map to individual chest guard items
          const sizeNumber = size.replace('size', '');
          const chestGuardId = `chestGuardSize${sizeNumber}${color.charAt(0).toUpperCase() + color.slice(1)}`;
          if (!cartItems[chestGuardId]) cartItems[chestGuardId] = 0;
          cartItems[chestGuardId] += quantity * sizeQuantity;
        });
      } else if (type === 'judgeOnly') {
        // Judge Only
        if (system.judgeBoxes > 0) {
          if (!cartItems['judgeBox']) cartItems['judgeBox'] = 0;
          cartItems['judgeBox'] += quantity * system.judgeBoxes;
        }
      }
    });
    onAddToCart(cartItems);
    setSelectedSystems([]);
  };
  // Helper function to map individual size to size group for pricing
  const getSizeGroup = size => {
    if (size === 'size0' || size === 'size1') {
      return 'size01';
    } else if (size === 'size2' || size === 'size3') {
      return 'size23';
    } else if (size === 'size4' || size === 'size5') {
      return 'size45';
    }
    return size;
  };
  const getChestGuardPrice = size => {
    // Map individual sizes to their price groups
    const sizeGroup = getSizeGroup(size);
    if (sizeGroup === 'size01') {
      return priceType === 'wholesale' ? 706.01 : 847.22;
    } else if (sizeGroup === 'size23') {
      return priceType === 'wholesale' ? 726.29 : 871.55;
    } else if (sizeGroup === 'size45') {
      return priceType === 'wholesale' ? 748.55 : 898.26;
    }
    return 0;
  };
  const getChestGuardSizeText = sizeCode => {
    switch (sizeCode) {
      case 'size0':
        return 'Size 0';
      case 'size1':
        return 'Size 1';
      case 'size2':
        return 'Size 2';
      case 'size3':
        return 'Size 3';
      case 'size4':
        return 'Size 4';
      case 'size5':
        return 'Size 5';
      default:
        return sizeCode;
    }
  };
  // Check if there's an imbalance in red/blue chest guards of the same size
  const checkColorImbalance = chestGuardSizes => {
    const sizeColorCount = {};
    // Count quantities by size and color
    chestGuardSizes.forEach(({
      size,
      color,
      quantity
    }) => {
      if (!sizeColorCount[size]) {
        sizeColorCount[size] = {
          red: 0,
          blue: 0
        };
      }
      sizeColorCount[size][color] += quantity;
    });
    // Check for imbalances
    for (const size in sizeColorCount) {
      const {
        red,
        blue
      } = sizeColorCount[size];
      if (red !== blue) {
        return true;
      }
    }
    return false;
  };
  // Check if there's an imbalance in red/blue head gears of the same size
  const checkHeadGearColorImbalance = headGearSizes => {
    const sizeColorCount = {};
    // Count quantities by size and color
    headGearSizes.forEach(({
      size,
      color,
      quantity
    }) => {
      if (!sizeColorCount[size]) {
        sizeColorCount[size] = {
          red: 0,
          blue: 0
        };
      }
      sizeColorCount[size][color] += quantity;
    });
    // Check for imbalances
    for (const size in sizeColorCount) {
      const {
        red,
        blue
      } = sizeColorCount[size];
      if (red !== blue) {
        return true;
      }
    }
    return false;
  };
  return <div className="mb-8" data-id="element-236">
      <h2 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-237">
        System Configuration
      </h2>
      <div className="flex flex-wrap gap-4 mb-6" data-id="element-238">
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => addSystem('full')} data-id="element-239">
          Add Full Ring
        </button>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => addSystem('judgeAndBody')} data-id="element-240">
          Add Judge and Body
        </button>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => addSystem('bodyOnly')} data-id="element-241">
          Add Body Only
        </button>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => addSystem('judgeOnly')} data-id="element-242">
          Add Judge Only
        </button>
      </div>
      {selectedSystems.length > 0 && <div className="space-y-6" data-id="element-243">
          {selectedSystems.map((system, index) => <div key={system.id} className="border rounded-lg p-4 bg-gray-50" data-id="element-244">
              <div className="flex justify-between items-center mb-4" data-id="element-245">
                <div className="flex items-center" data-id="element-246">
                  <h3 className="text-xl font-semibold mr-3" data-id="element-247">
                    {system.type === 'full' && 'Full Ring'}
                    {system.type === 'judgeAndBody' && 'Judge and Body'}
                    {system.type === 'bodyOnly' && 'Body Only'}
                    {system.type === 'judgeOnly' && 'Judge Only'}
                  </h3>
                </div>
                <button className="text-red-500 hover:text-red-700" onClick={() => removeSystem(system.id)} data-id="element-248">
                  Remove
                </button>
              </div>
              <div className="mb-4" data-id="element-249">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-250">
                  Quantity:
                </label>
                <input type="number" min="1" value={system.quantity} onChange={e => updateSystemQuantity(system.id, e.target.value)} className="border rounded-md px-3 py-2 w-24" data-id="element-251" />
              </div>
              {/* Head Gear Sizes Section for Full Ring */}
              {system.type === 'full' && <div className="mb-6" data-id="element-252">
                  <h4 className="font-medium mb-2" data-id="element-253">Head Gear Sizes:</h4>
                  <div className="border rounded-lg overflow-hidden max-w-md" data-id="element-254">
                    <table className="w-full text-sm text-left" data-id="element-255">
                      <thead className="bg-gray-50 border-b" data-id="element-256">
                        <tr data-id="element-257">
                          <th className="px-4 py-2 font-medium text-gray-700" data-id="element-258">
                            Size
                          </th>
                          <th className="px-4 py-2 font-medium text-gray-700 bg-red-50" data-id="element-259">
                            <div className="flex items-center justify-center" data-id="element-260">
                              <div className="w-3 h-3 rounded-full bg-red-600 mr-2" data-id="element-261"></div>
                              Red
                            </div>
                          </th>
                          <th className="px-4 py-2 font-medium text-gray-700 bg-blue-50" data-id="element-262">
                            <div className="flex items-center justify-center" data-id="element-263">
                              <div className="w-3 h-3 rounded-full bg-blue-600 mr-2" data-id="element-264"></div>
                              Blue
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200" data-id="element-265">
                        {['XS', 'S', 'M', 'L', 'XL'].map(size => {
                  const redQty = system.headGearSizes.find(i => i.size === size && i.color === 'red')?.quantity || 0;
                  const blueQty = system.headGearSizes.find(i => i.size === size && i.color === 'blue')?.quantity || 0;
                  return <tr key={size} className="hover:bg-gray-50" data-id="element-266">
                              <td className="px-4 py-2 font-medium text-gray-900" data-id="element-267">
                                {size}
                              </td>
                              <td className="px-4 py-2 bg-red-50/30 text-center" data-id="element-268">
                                <input type="number" min="0" value={redQty} onChange={e => {
                        const val = parseInt(e.target.value) || 0;
                        if (val > 0) addHeadGearSize(system.id, size, 'red', val);else removeHeadGearSize(system.id, size, 'red');
                      }} className="border rounded px-2 py-1 w-16 text-center" data-id="element-269" />
                              </td>
                              <td className="px-4 py-2 bg-blue-50/30 text-center" data-id="element-270">
                                <input type="number" min="0" value={blueQty} onChange={e => {
                        const val = parseInt(e.target.value) || 0;
                        if (val > 0) addHeadGearSize(system.id, size, 'blue', val);else removeHeadGearSize(system.id, size, 'blue');
                      }} className="border rounded px-2 py-1 w-16 text-center" data-id="element-271" />
                              </td>
                            </tr>;
                })}
                      </tbody>
                    </table>
                  </div>
                  {system.headGearSizes.length > 0 && checkHeadGearColorImbalance(system.headGearSizes) && <div className="text-red-500 text-sm mt-2 max-w-md" data-id="element-272">
                        Warning: In a real competition, both red and blue
                        players must wear the same size head gear. Please ensure
                        you have an equal number of red and blue head gears for
                        each size.
                      </div>}
                </div>}
              {/* Chest Guard Sizes Section */}
              {(system.type === 'full' || system.type === 'judgeAndBody' || system.type === 'bodyOnly') && <div className="mb-6" data-id="element-273">
                  <h4 className="font-medium mb-2" data-id="element-274">Chest Guard Sizes:</h4>
                  <div className="border rounded-lg overflow-hidden max-w-md" data-id="element-275">
                    <table className="w-full text-sm text-left" data-id="element-276">
                      <thead className="bg-gray-50 border-b" data-id="element-277">
                        <tr data-id="element-278">
                          <th className="px-4 py-2 font-medium text-gray-700" data-id="element-279">
                            Size
                          </th>
                          <th className="px-4 py-2 font-medium text-gray-700 bg-red-50" data-id="element-280">
                            <div className="flex items-center justify-center" data-id="element-281">
                              <div className="w-3 h-3 rounded-full bg-red-600 mr-2" data-id="element-282"></div>
                              Red
                            </div>
                          </th>
                          <th className="px-4 py-2 font-medium text-gray-700 bg-blue-50" data-id="element-283">
                            <div className="flex items-center justify-center" data-id="element-284">
                              <div className="w-3 h-3 rounded-full bg-blue-600 mr-2" data-id="element-285"></div>
                              Blue
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200" data-id="element-286">
                        {['size0', 'size1', 'size2', 'size3', 'size4', 'size5'].map((size, idx) => {
                  const redQty = system.chestGuardSizes.find(i => i.size === size && i.color === 'red')?.quantity || 0;
                  const blueQty = system.chestGuardSizes.find(i => i.size === size && i.color === 'blue')?.quantity || 0;
                  // Add a subtle thicker border between price tiers (0-1, 2-3, 4-5)
                  const isTierBoundary = idx === 1 || idx === 3;
                  return <tr key={size} className={`hover:bg-gray-50 ${isTierBoundary ? 'border-b-2 border-b-gray-300' : ''}`} data-id="element-287">
                              <td className="px-4 py-2 font-medium text-gray-900" data-id="element-288">
                                {getChestGuardSizeText(size)}
                              </td>
                              <td className="px-4 py-2 bg-red-50/30 text-center" data-id="element-289">
                                <input type="number" min="0" value={redQty} onChange={e => {
                        const val = parseInt(e.target.value) || 0;
                        if (val > 0) addChestGuardSize(system.id, size, 'red', val);else removeChestGuardSize(system.id, size, 'red');
                      }} className="border rounded px-2 py-1 w-16 text-center" data-id="element-290" />
                              </td>
                              <td className="px-4 py-2 bg-blue-50/30 text-center" data-id="element-291">
                                <input type="number" min="0" value={blueQty} onChange={e => {
                        const val = parseInt(e.target.value) || 0;
                        if (val > 0) addChestGuardSize(system.id, size, 'blue', val);else removeChestGuardSize(system.id, size, 'blue');
                      }} className="border rounded px-2 py-1 w-16 text-center" data-id="element-292" />
                              </td>
                            </tr>;
                })}
                      </tbody>
                    </table>
                  </div>
                  {system.chestGuardSizes.length > 0 && checkColorImbalance(system.chestGuardSizes) && <div className="text-red-500 text-sm mt-2 max-w-md" data-id="element-293">
                        Warning: In a real competition, both red and blue
                        players must wear the same size chest guard. Please
                        ensure you have an equal number of red and blue chest
                        guards for each size.
                      </div>}
                </div>}
              {/* Number of Judge Boxes */}
              {(system.type === 'full' || system.type === 'judgeAndBody' || system.type === 'judgeOnly') && <div className="mb-6" data-id="element-294">
                  <h4 className="font-medium mb-2" data-id="element-295">Number of Judge Boxes:</h4>
                  <input type="number" min="0" value={system.judgeBoxes || 0} onChange={e => updateSystemJudgeBoxes(system.id, e.target.value)} className="border rounded-md px-3 py-2 w-24" data-id="element-296" />
                </div>}
              {/* Number of Chargers */}
              {(system.type === 'full' || system.type === 'judgeAndBody' || system.type === 'bodyOnly') && <div className="mb-6" data-id="element-297">
                  <h4 className="font-medium mb-2" data-id="element-298">Number of Chargers:</h4>
                  <input type="number" min="1" value={system.chargers || 1} onChange={e => updateSystemChargers(system.id, e.target.value)} className="border rounded-md px-3 py-2 w-24" data-id="element-299" />
                </div>}
              <div className="text-sm text-gray-600" data-id="element-300">
                <h4 className="font-medium mb-1" data-id="element-301">
                  Included in this configuration:
                </h4>
                <ul className="list-disc pl-5" data-id="element-302">
                  <li data-id="element-303">Gen 3 Software</li>
                  <li data-id="element-304">Receiver</li>
                  {(system.type === 'full' || system.type === 'judgeAndBody' || system.type === 'bodyOnly') && <li data-id="element-305">2 Gen 3 Chest Guard Transmitters</li>}
                  {system.type === 'full' && <li data-id="element-306">2 Gen 3 Head Transmitters</li>}
                </ul>
              </div>
            </div>)}
          <div className="mt-6" data-id="element-307">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={handleAddToQuote} data-id="element-308">
              Add to Quote
            </button>
          </div>
        </div>}
    </div>;
}